import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, MapPin, Globe, Users, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { GeographyMap } from "@/components/GeographyMap";
import { fetchGeographyStats, type GeographyLocation } from "@/api/geography";
import { useCountAnimation } from "@/hooks/useCountAnimation";
import { PageHeader } from "@/components/PageHeader";
import { usePageSeo } from "@/hooks/usePageSeo";
import { statsKeys } from "@/api/queryKeys";

interface CountryGroup {
  country: string;
  cities: GeographyLocation[];
  totalParticipants: number;
}

function groupByCountry(locations: GeographyLocation[]): CountryGroup[] {
  const map = new Map<string, GeographyLocation[]>();
  for (const loc of locations) {
    const existing = map.get(loc.country);
    if (existing) {
      existing.push(loc);
    } else {
      map.set(loc.country, [loc]);
    }
  }

  return Array.from(map.entries())
    .map(([country, cities]) => ({
      country,
      cities,
      totalParticipants: cities.reduce(
        (sum, c) => sum + c.uniqueParticipants,
        0,
      ),
    }))
    .sort((a, b) => b.totalParticipants - a.totalParticipants);
}

function StatPill({
  icon,
  value,
  children,
}: {
  icon: React.ReactNode;
  value: number;
  children: React.ReactNode;
}) {
  // grid-cols-3 parent gives equal thirds on mobile (can't overflow);
  // sm:flex-initial lets the pill hug its content on desktop.
  // min-w-0 lets the long label truncate instead of pushing width.
  return (
    <div className="flex sm:flex-initial min-w-0 items-center justify-center gap-1.5 sm:gap-2 rounded-full border bg-card px-2.5 sm:px-3.5 py-2">
      <span className="hidden sm:inline-flex shrink-0">{icon}</span>
      <span className="text-base md:text-lg font-bold text-foreground">
        {value}
      </span>
      <span className="text-xs text-muted-foreground truncate">{children}</span>
    </div>
  );
}

const Geography = () => {
  usePageSeo(
    "Where Quizrun Runs — London, Europe & Worldwide Event Locations",
    "Quizrun events run in London (Hyde Park, Southwark Park) and across 50+ cities in 10+ countries — from Berlin and Prague to Tbilisi, Yerevan and Haifa.",
  );
  const { data: stats, isLoading } = useQuery({
    queryKey: statsKeys.geography,
    queryFn: fetchGeographyStats,
    staleTime: 10 * 60 * 1000,
  });

  const topCountries = useMemo(() => {
    if (!stats?.locations) return [];
    return groupByCountry(stats.locations).slice(0, 5);
  }, [stats?.locations]);

  const animatedParticipants = useCountAnimation({
    end: stats?.totalUniqueFinishers ?? 0,
    duration: 2000,
    enabled: !!stats,
  });

  const animatedCities = useCountAnimation({
    end: stats?.totalCities ?? 0,
    duration: 2000,
    enabled: !!stats,
  });

  const animatedCountries = useCountAnimation({
    end: stats?.totalCountries ?? 0,
    duration: 2000,
    enabled: !!stats,
  });

  return (
    <div className="flex flex-col">
      <PageHeader
        eyebrow="Geography"
        title="Around the world"
        intro={
          <>
            <p>Quizrun participants from parks, cities, and countries worldwide.</p>
            {/* Narrative arc for local rank: origin (London) → spread. Keep park
                detail on /london, not here. */}
            <p className="mt-3 text-sm">
              It started in{" "}
              <Link to="/london" className="font-medium text-primary hover:underline">
                London
              </Link>{" "}
              and now runs across Berlin, Prague, New York, Tbilisi, Yerevan,
              Limassol and Haifa — plus remote events you can run anywhere.
            </p>
          </>
        }
      />
      <main className="flex-1 container mx-auto px-4 md:px-8 pb-16">
        <div className="max-w-7xl mx-auto">

          {isLoading ? (
            <div className="flex items-center justify-center h-[400px] md:h-[600px] bg-card rounded-lg border">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : stats ? (
            <>
              {/* Compact stat strip — keep chrome small so the map leads */}
              <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-4">
                <div className="grid grid-cols-3 flex-1 sm:flex sm:flex-initial gap-1.5 sm:gap-3 min-w-0">
                  <StatPill
                    icon={<Users className="h-4 w-4 text-primary" />}
                    value={animatedParticipants}
                  >
                    {/* long label on desktop, short on mobile to stay equal-width */}
                    <span className="hidden md:inline">unique participants</span>
                    <span className="md:hidden">participants</span>
                  </StatPill>
                  <StatPill
                    icon={<MapPin className="h-4 w-4 text-primary" />}
                    value={animatedCities}
                  >
                    cities
                  </StatPill>
                  <StatPill
                    icon={<Globe className="h-4 w-4 text-primary" />}
                    value={animatedCountries}
                  >
                    countries
                  </StatPill>
                </div>

                {topCountries.length > 0 && (
                  <div className="flex items-center gap-1.5 min-w-0 rounded-full border bg-card px-3.5 py-2 text-xs text-muted-foreground">
                    <span className="font-bold text-foreground shrink-0">Top:</span>
                    <span className="truncate">
                      {topCountries
                        .map((g) => `${g.country} ${g.totalParticipants}`)
                        .join(" · ")}
                    </span>
                  </div>
                )}
              </div>

              <GeographyMap locations={stats.locations} />

              {/* CTA */}
              <div className="mt-8 rounded-xl border bg-card p-6 md:p-8 flex flex-col md:flex-row items-center gap-4 md:gap-6">
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-lg font-bold text-foreground">
                    Your city could be next
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    We're looking for local organisers to bring Quizrun to new
                    parks worldwide.
                  </p>
                </div>
                <Link
                  to="/partnership"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 transition-colors shrink-0"
                >
                  Partner with us
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </>
          ) : (
            <div className="text-center text-muted-foreground">
              Unable to load geography data
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Geography;
